import React, { useEffect, useState } from 'react';
import { IEntity } from '../../../../../../../types/api/entities/IEntity';
import { KeyEvent } from '../../../../../../../types/KeyEvent';

export type Selected<T> = {
  event: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLDivElement>;
  selectedItem?: T | string;
};

export default function usePopupKeyboardNavigation<T extends IEntity>(
  listItems: T[],
  confirmSelection: (item: Selected<T>) => void,
  closePopup: () => void,
  openPopup: () => void,
  resetState: () => void,
  popupOpen: boolean,
  inputValue: string | undefined,
) {
  const [markedItem, setMarkedItem] = useState<T>();

  // reset marked item if the popup closes.
  useEffect(() => {
    if (!popupOpen) {
      setMarkedItem(undefined);
    }
  }, [popupOpen]);

  const currentIndex = listItems.findIndex(e => e.id === markedItem?.id);

  const getNextItemId = (direction: -1 | 1) => {
    if (listItems.length > 0) {
      if (currentIndex === -1) {
        const [first] = listItems;
        const [last] = listItems.reverse();
        return direction === 1 ? first : last;
      }
      const nextIndex = (currentIndex + listItems.length + direction) % listItems.length;
      return listItems[nextIndex];
    }
    return undefined;
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.code) {
      case KeyEvent.ArrowUp:
        if (popupOpen || inputValue != null) {
          event.stopPropagation();
          event.preventDefault();

          setMarkedItem(getNextItemId(-1));
        }
        break;
      case KeyEvent.ArrowDown:
        if (!popupOpen && listItems.length > 0) {
          openPopup();
        } else {
          event.stopPropagation();
          event.preventDefault();

          setMarkedItem(getNextItemId(1));
        }
        break;
      case KeyEvent.Enter:
        if (popupOpen && markedItem) {
          // event.stopPropagation();
          confirmSelection({ event, selectedItem: listItems.find(e => e.id === markedItem.id) });
        } else if (!markedItem) {
          confirmSelection({ event, selectedItem: inputValue });
        } else {
          confirmSelection({ event });
        }
        break;
      case KeyEvent.Escape:
        setMarkedItem(undefined);
        if (popupOpen) {
          // stop propagation, esc click has a dedicated goal
          // which can be finished in this context: closing the results.
          // prevents modals. from closing on keydown with esc
          event.stopPropagation();
          closePopup();
        } else if (inputValue != null) {
          event.stopPropagation();
          resetState();
          confirmSelection({ event });
        }
        break;
      default:
        break;
    }
  };

  return { onKeyDown, markedItem };
}
